import { inject, Injectable } from '@angular/core';
import { HttpTransportType, HubConnectionBuilder, type HubConnection } from '@microsoft/signalr';
import { AppEvent, isAppEventType } from '../models/app-event.models';
import { SIGNALR_CONFIG } from './provide-signalr';
import { reconnectDelayMs } from './signalr-reconnect';

function parseAppEvent(payload: string): AppEvent | null {
  try {
    const parsed: unknown = JSON.parse(payload);
    if (!parsed || typeof parsed !== 'object' || Array.isArray(parsed)) {
      return null;
    }

    const type = 'type' in parsed ? parsed.type : undefined;
    if (!isAppEventType(type)) {
      return null;
    }

    return parsed as AppEvent;
  } catch {
    return null;
  }
}

@Injectable({
  providedIn: 'root',
})
export class SignalRService {
  private readonly config = inject(SIGNALR_CONFIG, { optional: true });
  private connection?: HubConnection;
  private status: 'Open' | 'Closed' | 'Connecting' = 'Closed';
  private reconnectTimer?: ReturnType<typeof setTimeout>;
  private consecutiveFailures = 0;

  /** Generate access token and hub URL based on last processed event */
  private negotiateUrl(): Promise<{
    url: string;
    accessToken: string;
  }> {
    const config = this.config!;
    const stored = localStorage.getItem(config.cursorStorageKey);
    const lastEventId = stored ? stored : null;
    return config.negoatiateUrl(lastEventId);
  }

  private clearReconnectTimer(): void {
    if (this.reconnectTimer !== undefined) {
      clearTimeout(this.reconnectTimer);
      this.reconnectTimer = undefined;
    }
  }

  private scheduleReconnect(cb: (event: AppEvent) => void): void {
    if (this.status === 'Closed' || this.reconnectTimer !== undefined) {
      return;
    }

    void this.connection?.stop();
    this.consecutiveFailures += 1;
    const delayMs = reconnectDelayMs(this.consecutiveFailures);
    this.reconnectTimer = setTimeout(() => {
      this.reconnectTimer = undefined;
      if (this.status === 'Closed') {
        return;
      }
      this.connectAppEvents(cb);
    }, delayMs);
  }

  private async connectAppEvents(cb: (event: AppEvent) => void): Promise<void> {
    if (this.status === 'Closed') return;
    const config = this.config!;
    this.clearReconnectTimer();
    void this.connection?.stop();
    this.status = 'Connecting';
    const { url, accessToken } = await this.negotiateUrl();
    const connection = new HubConnectionBuilder()
      .withUrl(url, {
        transport: HttpTransportType.ServerSentEvents,
        accessTokenFactory: () => accessToken,
      })
      .build();
    this.connection = connection;
    const hubMethod = config.hubMethod || 'AppEvent';
    connection.on(hubMethod, (payload: string) => {
      const event = parseAppEvent(payload);
      if (!event) return;
      if (event.id) localStorage.setItem(config.cursorStorageKey, event.id);
      cb(event);
    });
    connection.onclose(() => {
      this.scheduleReconnect(cb);
    });
    void connection.start().then(
      () => {
        if (this.status !== 'Closed') {
          this.status = 'Open';
          this.consecutiveFailures = 0;
        }
      },
      () => {
        this.scheduleReconnect(cb);
      },
    );
  }

  /**
   * Requests App Event SignalR, locked to one connection per user
   *
   * @param cb - event callback
   * @returns close function, closes app event hub
   */
  public requestAppEventsSignalR(cb: (event: AppEvent) => void): () => void {
    const config = this.config;
    if (!config) {
      return () => undefined;
    }
    const abortController = new AbortController();
    let close = () => abortController.abort();
    navigator.locks.request(
      config.lockName,
      { signal: abortController.signal },
      async () =>
        new Promise((resolve) => {
          this.status = 'Connecting';
          this.connectAppEvents(cb);
          close = () => {
            this.status = 'Closed';
            this.consecutiveFailures = 0;
            this.clearReconnectTimer();
            void this.connection?.stop();
            abortController.abort();
            resolve(null);
          };
        }),
    );
    return () => close();
  }
}
